import React from 'react'
import cookies from 'js-cookie'
import { Experiment, Variation } from '@deal/experiment-js'
import { FancyModal, TextField, Button } from '@deal/components'
import { withIdentity, WithIdentityProps } from '#src/containers/Identity'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import { UpdateProfileDataComponent, ContactType } from '#src/generated/graphql'
import styles from './styles.css'
import config from '#src/config'
import { toast } from 'react-toastify'
import { isEmailValid as isEmailValidRegex } from '#src/services/email'

interface State {
  isOpen?: boolean
  isEmailValid?: boolean
  email?: string
  firstName?: string
  lastName?: string
}

class UserProfileCollectionModal extends React.Component<
  WithAnalyticsProps & WithIdentityProps,
  State
> {
  constructor(props: WithAnalyticsProps & WithIdentityProps) {
    super(props)
    const { myself } = props

    this.state = {
      isOpen: true,
      isEmailValid: false,
      email: '',
      // Populate names, in case they already exist
      firstName: (myself && myself.user.firstName) || '',
      lastName: (myself && myself.user.lastName) || ''
    }
  }

  public componentDidMount() {
    const { myself, analytics } = this.props
    if (myself && analytics) {
      /**
       * Make sure they haven't seen the modal and they're logged in
       * so we don't incorrectly track this.
       */
      if (!this.handleHasUserSeenModal()) {
        analytics.userProfileCollectionModalTriggered({
          user_id: myself.user.id
        })
      }
    }
  }

  public handleHasUserSeenModal = () => {
    /**
     * We don't show the modal more than once in 12 hours if dismissed
     */
    return cookies.get('deal_data_collection_modal_viewed')
  }

  public handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target: { value, name } } = event
    const { isEmailValid } = this.state
    this.setState({
      [name]: value,
      isEmailValid: name === 'email' ? isEmailValidRegex(value) : isEmailValid
    })
  }

  public handleCloseModal = () => {
    cookies.set('deal_data_collection_modal_viewed', 'true', {
      // Expires in 12 hours
      expires: 0.5,
      domain: config.get('consumer.clientSideCookieDomain')
    })

    this.setState({ isOpen: false })
  }

  public handleSuccessUpdate = () => {
    const { myself, analytics } = this.props
    if (myself && analytics) {
      analytics.userProfileCollectionModalSubmitted({
        user_id: myself.user.id
      })
    }

    toast.success('Successfully updated data.')
    this.setState({ isOpen: false })
  }

  public render() {
    const { myself } = this.props
    const { isOpen, email, firstName, lastName, isEmailValid } = this.state

    const doesUserHasEmail = !!(
      myself && myself.user.contacts.find(contact => contact.type === ContactType.EMAIL)
    )

    /**
     * Always hide this modal if :
     * - Not logged in
     * - The user already has an email
     * - The cookie still exists or if they're not logged in
     */
    if (!myself || doesUserHasEmail || this.handleHasUserSeenModal()) {
      return null
    }

    return (
      <Experiment id="complete-user-profile-modal" default="off">
        <Variation id="on">
          <UpdateProfileDataComponent
            variables={{
              input: {
                email,
                firstName,
                lastName
              }
            }}
            onCompleted={data => {
              if (data.updateUserProfile.errors && data.updateUserProfile.errors.length) {
                toast.error('Error updating your phone number! Please try again.')
              } else {
                this.handleSuccessUpdate()
              }
            }}
            onError={() => {
              toast.error('Error updating your phone number! Please try again.')
            }}
          >
            {(updateProfileData, { loading }) => (
              <FancyModal
                isOpen={!!isOpen}
                className={styles.modal}
                onRequestClose={this.handleCloseModal}
              >
                <div className={styles.container}>
                  <div className={styles.textContainer}>
                    <div className={styles.title}>Complete your account</div>
                    <div className={styles.subtitle}>
                      Enter your profile data below to complete your account and unlock expecial
                      offers.
                    </div>
                    <div className={styles.inputContainer}>
                      <TextField
                        label="Email"
                        labelHidden
                        placeholder="Email"
                        type="email"
                        name="email"
                        value={email}
                        display="standard"
                        // Prevent to show error before user starts typing
                        valid={email ? isEmailValid : undefined}
                        onChange={this.handleChange}
                      />
                      <TextField
                        label="First name"
                        labelHidden
                        placeholder="First name"
                        name="firstName"
                        value={firstName}
                        display="standard"
                        onChange={this.handleChange}
                      />
                      <TextField
                        label="Last name"
                        labelHidden
                        placeholder="Last name"
                        name="lastName"
                        value={lastName}
                        display="standard"
                        onChange={this.handleChange}
                      />
                    </div>
                  </div>
                  <div className={styles.footer}>
                    <Button
                      className={styles.button}
                      disabled={!isEmailValid || loading}
                      onClick={() => updateProfileData()}
                    >
                      Confirm Profile
                    </Button>
                  </div>
                </div>
              </FancyModal>
            )}
          </UpdateProfileDataComponent>
        </Variation>
        <Variation id="off" />
      </Experiment>
    )
  }
}

export default withIdentity(withAnalytics(UserProfileCollectionModal))
